import React, { useEffect } from 'react';
import { Box, Typography, Container, Grid, Stack, Chip } from '@mui/material';
import { keyframes } from '@emotion/react';
import {
  MdEmergency,
  MdCloud,
  MdFolderSpecial,
  MdHttps,
  MdVerifiedUser,
} from 'react-icons/md';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

const sections = [
  {
    icon: MdEmergency,
    title: 'Robot emergency stop',
    body:
      'Fleet robots support an emergency stop so operators can halt motion and UV cycles immediately in unexpected situations. Physical and software safeguards work together so the unit stops safely and status is visible in the admin portal.',
    accent: 'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)',
  },
  {
    icon: MdCloud,
    title: 'AWS high-level security',
    body:
      'Core services run on AWS with defense-in-depth: network isolation, encryption in transit and at rest, least-privilege access, and continuous monitoring aligned with cloud security best practices.',
    accent: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
  },
  {
    icon: MdFolderSpecial,
    title: 'Protected storage',
    body:
      'Operational data, media, and configuration are stored in protected buckets and databases with strict access policies, versioning where appropriate, and retention controls to reduce exposure.',
    accent: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
  },
  {
    icon: MdHttps,
    title: 'Secure API services',
    body:
      'Admin and device APIs use authenticated, token-based access (e.g. Bearer tokens), TLS endpoints, and rate-aware usage so only authorized clients and users can read or change system state.',
    accent: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  },
];

const trustPoints = ['Encryption in transit & at rest', 'Least-privilege access', 'Continuous monitoring'];

export default function SecurityCenter() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box sx={sx.root}>
      {/* Hero */}
      <Box sx={sx.heroWrap}>
        <Box sx={sx.heroGlowA} aria-hidden />
        <Box sx={sx.heroGlowB} aria-hidden />
        <Box sx={sx.heroGrid} aria-hidden />
        <Container maxWidth="lg" sx={sx.heroInner}>
          <Chip
            icon={<MdVerifiedUser style={{ fontSize: 16 }} />}
            label="Enterprise security posture"
            sx={sx.heroChip}
          />
          <Typography
            component="h1"
            sx={{
              ...sx.heroTitle,
              animation: `${fadeUp} 0.7s ease-out both`,
            }}
          >
            Security Center
          </Typography>
          <Typography
            variant="body1"
            sx={{
              ...sx.heroLead,
              animation: `${fadeUp} 0.7s ease-out 0.08s both`,
            }}
          >
            How we protect robots, data, and APIs across the Spotless AI admin experience—from on-device safety
            to cloud and API controls.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3, animation: `${fadeUp} 0.7s ease-out 0.16s both` }}>
            {trustPoints.map((t) => (
              <Box key={t} sx={sx.trustPill}>
                <Box sx={sx.trustDot} />
                <Typography variant="caption" sx={sx.trustText}>
                  {t}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* Cards */}
      <Container maxWidth="lg" sx={{ pb: { xs: 6, md: 10 }, pt: { xs: 0, md: 1 } }}>
        <Grid container spacing={{ xs: 2.5, md: 3 }}>
          {sections.map(({ icon: Icon, title, body, accent }, i) => (
            <Grid item xs={12} sm={6} key={title}>
              <Box
                sx={{
                  ...sx.card,
                  animation: `${fadeUp} 0.6s ease-out ${0.05 * (i + 1)}s both`,
                }}
              >
                <Box sx={{ ...sx.iconRing, background: accent }}>
                  <Icon size={26} color="#fff" />
                </Box>
                <Typography variant="overline" sx={sx.cardIndex}>
                  {String(i + 1).padStart(2, '0')}
                </Typography>
                <Typography variant="h6" component="h2" sx={sx.cardTitle}>
                  {title}
                </Typography>
                <Typography variant="body2" sx={sx.cardBody}>
                  {body}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Box sx={sx.bottomBand}>
        <Container maxWidth="md">
          <Typography variant="body2" sx={sx.bottomText}>
            Security practices evolve with your deployment. Always follow your organization’s policies and the latest
            product documentation.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}

const sx = {
  root: {
    minHeight: '100vh',
    bgcolor: '#f8fafc',
  },
  heroWrap: {
    position: 'relative',
    overflow: 'hidden',
    pt: { xs: 6, md: 9 },
    pb: { xs: 8, md: 10 },
    background: 'linear-gradient(165deg, #0f172a 0%, #1e1b4b 42%, #312e81 100%)',
  },
  heroGlowA: {
    position: 'absolute',
    top: '-20%',
    right: '-10%',
    width: { xs: '70%', md: '45%' },
    height: '80%',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(99, 102, 241, 0.45) 0%, transparent 70%)',
    filter: 'blur(40px)',
    pointerEvents: 'none',
  },
  heroGlowB: {
    position: 'absolute',
    bottom: '-25%',
    left: '-15%',
    width: '55%',
    height: '70%',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(236, 72, 153, 0.25) 0%, transparent 65%)',
    filter: 'blur(50px)',
    pointerEvents: 'none',
  },
  heroGrid: {
    position: 'absolute',
    inset: 0,
    opacity: 0.07,
    backgroundImage: `
      linear-gradient(rgba(255,255,255,0.15) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.15) 1px, transparent 1px)
    `,
    backgroundSize: '48px 48px',
    pointerEvents: 'none',
  },
  heroInner: {
    position: 'relative',
    zIndex: 1,
  },
  heroChip: {
    mb: 2,
    bgcolor: 'rgba(255,255,255,0.1)',
    color: '#e2e8f0',
    border: '1px solid rgba(255,255,255,0.18)',
    fontWeight: 600,
    letterSpacing: '0.04em',
    '& .MuiChip-icon': { color: '#a5b4fc' },
  },
  heroTitle: {
    m: 0,
    mb: 2,
    fontSize: { xs: '2.25rem', sm: '2.75rem', md: '3.25rem' },
    fontWeight: 800,
    letterSpacing: '-0.04em',
    lineHeight: 1.08,
    color: '#fff',
    maxWidth: '14ch',
  },
  heroLead: {
    color: 'rgba(226, 232, 240, 0.92)',
    lineHeight: 1.75,
    maxWidth: 'min(640px, 100%)',
    fontSize: { xs: '1.05rem', md: '1.125rem' },
    fontWeight: 400,
  },
  trustPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1,
    py: 0.75,
    px: 1.5,
    borderRadius: 999,
    bgcolor: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.12)',
  },
  trustDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    bgcolor: '#34d399',
    boxShadow: '0 0 0 3px rgba(52, 211, 153, 0.25)',
  },
  trustText: {
    color: '#e2e8f0',
    fontWeight: 600,
    letterSpacing: '0.02em',
    fontSize: '0.75rem',
  },
  card: {
    height: '100%',
    p: { xs: 2.5, md: 3 },
    borderRadius: '20px',
    background: 'rgba(255, 255, 255, 0.92)',
    border: '1px solid rgba(15, 23, 42, 0.06)',
    boxShadow: '0 4px 6px -1px rgba(15, 23, 42, 0.06), 0 24px 48px -12px rgba(15, 23, 42, 0.08)',
    transition: 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.35s ease',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '3px',
      background: 'linear-gradient(90deg, #6366f1, #a855f7, #ec4899)',
      opacity: 0,
      transition: 'opacity 0.35s ease',
    },
    '&:hover': {
      transform: 'translateY(-6px)',
      boxShadow: '0 12px 24px -4px rgba(15, 23, 42, 0.12), 0 32px 64px -16px rgba(79, 70, 229, 0.15)',
      '&::before': { opacity: 1 },
    },
  },
  iconRing: {
    width: 52,
    height: 52,
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    mb: 2,
    boxShadow: '0 8px 20px rgba(15, 23, 42, 0.15)',
  },
  cardIndex: {
    color: '#94a3b8',
    fontWeight: 700,
    letterSpacing: '0.2em',
    mb: 1,
    display: 'block',
  },
  cardTitle: {
    fontWeight: 700,
    color: '#0f172a',
    mb: 1.25,
    fontSize: '1.15rem',
    letterSpacing: '-0.02em',
    lineHeight: 1.35,
  },
  cardBody: {
    color: '#64748b',
    lineHeight: 1.75,
    fontSize: '0.9375rem',
  },
  bottomBand: {
    py: 4,
    borderTop: '1px solid rgba(15, 23, 42, 0.06)',
    background: 'linear-gradient(180deg, #f1f5f9 0%, #f8fafc 100%)',
  },
  bottomText: {
    color: '#64748b',
    textAlign: 'center',
    lineHeight: 1.7,
    maxWidth: 560,
    mx: 'auto',
  },
};
